import React from 'react';
import { Stack } from '@mui/material';
// own
import { createSx } from '../../../../utils';
import { useAttachmentsStateContext } from '../../provider/AttachmentsStateProvider';
import Button from '../../../Button';
import { Attachment } from '../..';
import { MAX_ATTACHMENTS_COUNT, ATTACHMENTS_SIZE } from './const';
import AttachmentThumbnail from './components/AttachmentThumbnail';

const sx = createSx({
  moreAttachmentsCounter: {
    width: ATTACHMENTS_SIZE,
    height: ATTACHMENTS_SIZE,
    minWidth: ATTACHMENTS_SIZE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'primary.main',
  },
});

export interface AttachmentComponentProps<T extends Attachment> {
  attachment: T;
  onClick: () => void;
}

export type AttachmentComponent<T extends Attachment> = (props: AttachmentComponentProps<T>) => JSX.Element;

interface Props<T extends Attachment> {
  maxAttachmentCount?: number;
  AttachmentComponent?: AttachmentComponent<T>;
}

const AttachmentsList = <T extends Attachment, >({
  maxAttachmentCount = MAX_ATTACHMENTS_COUNT,
  AttachmentComponent,
}: Props<T>) => {
  const { attachmentsList, setCurrentAttachment } = useAttachmentsStateContext<T>();
  const moreAttachmentsCount = attachmentsList.size - maxAttachmentCount;

  const getAttachmentNodes = () => {
    const attachments = [];
    let currentAttachment = attachmentsList.head;
    while (attachments.length < maxAttachmentCount && currentAttachment) {
      attachments.push(currentAttachment);
      currentAttachment = currentAttachment.next;
    }
    return attachments;
  };

  return (
    <Stack direction='row' spacing={1} data-test-id='attachments-list'>
      {getAttachmentNodes().map((attachmentNode) => {
        const attachment = attachmentNode.value;

        if (AttachmentComponent) {
          return (
            <AttachmentComponent
              key={attachment.url}
              attachment={attachment}
              onClick={() => setCurrentAttachment(attachmentNode)}
            />
          );
        }

        return (
          <AttachmentThumbnail
            attachment={attachment}
            onClick={() => setCurrentAttachment(attachmentNode)}
            key={attachment.url}
          />
        );
      })}
      {moreAttachmentsCount > 0 &&
        <Button
          variant='outlined'
          sx={sx.moreAttachmentsCounter}
          onClick={() => setCurrentAttachment(attachmentsList.head)}
          data-test-id='more-attachments-counter'
        >
          +{moreAttachmentsCount}
        </Button>}
    </Stack>
  );
};

export default AttachmentsList;